import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { MessageSquare, Sparkles, Search, Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

const Tools = () => {
  const [params] = useSearchParams();
  const initial = params.get("tab") || "reply";
  const [tab, setTab] = useState(initial);
  useEffect(() => { if (initial) setTab(initial); }, [initial]);
  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="overline mb-2">INTERACTIVE TOOLS</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-6" data-testid="tools-title">Real situations. <span className="italic text-amber-500">Real answers.</span></h1>

      <div className="flex flex-wrap gap-2 mb-8">
        {[
          { id: "reply", label: "Text Reply", icon: MessageSquare },
          { id: "opener", label: "Opener", icon: Sparkles },
          { id: "analyze", label: "Situation Analyzer", icon: Search },
          { id: "reconnect", label: "Reconnect (After No-Contact)", icon: HeartHandshake },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 transition-colors ${
              tab === t.id ? "bg-amber-500 text-white" : "bg-white/5 text-neutral-400 hover:text-white border border-white/10"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "reply" && <TextReplyTool />}
      {tab === "opener" && <OpenerTool />}
      {tab === "analyze" && <AnalyzerTool />}
      {tab === "reconnect" && <ReconnectTool />}
    </div>
  );
};

const ResultCard = ({ text }) => (
  <div className="card-surface p-6 mt-6 whitespace-pre-wrap leading-relaxed text-neutral-200" data-testid="tool-result">
    {text}
  </div>
);

const TextReplyTool = () => {
  const [herMsg, setHerMsg] = useState("");
  const [context, setContext] = useState("");
  const [tone, setTone] = useState("playful");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const run = async () => {
    if (!herMsg.trim()) return;
    setLoading(true); setResult("");
    try {
      const r = await api.post("/tools/text-reply", { her_message: herMsg, context, tone });
      setResult(r.data.result);
    } catch (e) { toast.error("Failed to generate replies."); }
    setLoading(false);
  };

  return (
    <div className="card-surface p-8">
      <h2 className="font-serif text-2xl mb-2">Text Reply Generator</h2>
      <p className="text-neutral-400 mb-6">Paste her message. Get 3 reply options with the reasoning.</p>

      <label className="overline text-neutral-500">Her message</label>
      <textarea
        value={herMsg}
        onChange={(e) => setHerMsg(e.target.value)}
        rows={3}
        placeholder="e.g. Hey! Sorry been swamped. How's your week?"
        className="w-full mt-2 mb-5 p-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
        data-testid="reply-her-input"
      />

      <label className="overline text-neutral-500">Context (optional)</label>
      <textarea
        value={context}
        onChange={(e) => setContext(e.target.value)}
        rows={2}
        placeholder="Met on Hinge, first date planned Thursday..."
        className="w-full mt-2 mb-5 p-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
        data-testid="reply-context-input"
      />

      <label className="overline text-neutral-500">Tone</label>
      <div className="flex flex-wrap gap-2 mt-2">
        {["playful", "confident", "sincere", "witty"].map((t) => (
          <button
            key={t}
            onClick={() => setTone(t)}
            className={`px-4 py-2 rounded-full text-sm border ${
              tone === t ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-white/10 text-neutral-400 hover:text-white"
            }`}
            data-testid={`tone-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <button onClick={run} disabled={loading} className="btn-amber mt-8 inline-flex items-center gap-2 disabled:opacity-60" data-testid="reply-generate-btn">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Generate replies
      </button>
      {result && <ResultCard text={result} />}
    </div>
  );
};

const OpenerTool = () => {
  const [sit, setSit] = useState("");
  const [platform, setPlatform] = useState("in-person");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const run = async () => {
    if (!sit.trim()) return;
    setLoading(true); setResult("");
    try {
      const r = await api.post("/tools/opener", { situation: sit, platform });
      setResult(r.data.result);
    } catch { toast.error("Failed to generate openers."); }
    setLoading(false);
  };

  return (
    <div className="card-surface p-8">
      <h2 className="font-serif text-2xl mb-2">Opener Generator</h2>
      <p className="text-neutral-400 mb-6">Charismatic openers built for the specific situation.</p>

      <label className="overline text-neutral-500">Situation</label>
      <textarea
        value={sit}
        onChange={(e) => setSit(e.target.value)}
        rows={3}
        placeholder="I saw her reading a book at a wine bar..."
        className="w-full mt-2 mb-5 p-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
        data-testid="opener-situation-input"
      />

      <label className="overline text-neutral-500">Platform</label>
      <div className="flex flex-wrap gap-2 mt-2">
        {[
          { id: "in-person", label: "In person" },
          { id: "text", label: "Text" },
          { id: "dating-app", label: "Dating app" },
        ].map((p) => (
          <button
            key={p.id}
            onClick={() => setPlatform(p.id)}
            className={`px-4 py-2 rounded-full text-sm border ${
              platform === p.id ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-white/10 text-neutral-400 hover:text-white"
            }`}
            data-testid={`platform-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <button onClick={run} disabled={loading} className="btn-amber mt-8 inline-flex items-center gap-2 disabled:opacity-60" data-testid="opener-generate-btn">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Generate openers
      </button>
      {result && <ResultCard text={result} />}
    </div>
  );
};

const AnalyzerTool = () => {
  const [sc, setSc] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const run = async () => {
    if (!sc.trim()) return;
    setLoading(true); setResult("");
    try {
      const r = await api.post("/tools/analyze", { scenario: sc });
      setResult(r.data.result);
    } catch { toast.error("Failed to analyze."); }
    setLoading(false);
  };

  return (
    <div className="card-surface p-8">
      <h2 className="font-serif text-2xl mb-2">Situation Analyzer</h2>
      <p className="text-neutral-400 mb-6">Describe what happened. Get an honest read and a next move.</p>

      <label className="overline text-neutral-500">Your situation</label>
      <textarea
        value={sc}
        onChange={(e) => setSc(e.target.value)}
        rows={5}
        placeholder="We had a great first date, kissed goodnight. I texted the next day and she replied but hasn't asked to meet again in 4 days..."
        className="w-full mt-2 mb-5 p-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
        data-testid="analyze-input"
      />

      <button onClick={run} disabled={loading} className="btn-amber mt-2 inline-flex items-center gap-2 disabled:opacity-60" data-testid="analyze-generate-btn">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Analyze
      </button>
      {result && <ResultCard text={result} />}
    </div>
  );
};

const ReconnectTool = () => {
  const [moment, setMoment] = useState("");
  const [timeSince, setTimeSince] = useState("");
  const [tone, setTone] = useState("warm");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const run = async () => {
    if (!moment.trim()) return;
    setLoading(true); setResult("");
    try {
      const r = await api.post("/tools/reconnect-message", {
        shared_moment: moment,
        time_broken_up: timeSince,
        tone,
      });
      setResult(r.data.result);
    } catch { toast.error("Failed to generate messages."); }
    setLoading(false);
  };

  return (
    <div className="card-surface p-8">
      <div className="overline text-amber-500 mb-2">POST 30-DAY NO-CONTACT</div>
      <h2 className="font-serif text-2xl mb-2">Reconnect Message Generator</h2>
      <p className="text-neutral-400 mb-6">
        Only after 30 days of no-contact. Reference one specific shared moment. One text. No follow-up. Respect her reply fully.
      </p>

      <div className="card-elevated p-4 border-l-2 border-amber-500 mb-6">
        <p className="text-sm text-neutral-300">
          <strong className="text-amber-400">Rule of thumb:</strong> If sending this message feels like negotiating or auditioning, you're not ready. Reach out from strength, not loneliness.
        </p>
      </div>

      <label className="overline text-neutral-500">Shared moment to reference</label>
      <textarea
        value={moment}
        onChange={(e) => setMoment(e.target.value)}
        rows={3}
        placeholder="e.g. The weekend we got lost on the hike in Big Sur and ended up at that cliff watching the sunset"
        className="w-full mt-2 mb-5 p-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
        data-testid="reconnect-moment-input"
      />

      <label className="overline text-neutral-500">Time since no-contact started</label>
      <input
        type="text"
        value={timeSince}
        onChange={(e) => setTimeSince(e.target.value)}
        placeholder="30 days, 45 days…"
        className="w-full mt-2 mb-5 px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
        data-testid="reconnect-time-input"
      />

      <label className="overline text-neutral-500">Tone</label>
      <div className="flex flex-wrap gap-2 mt-2">
        {["warm", "playful", "reflective", "casual"].map((t) => (
          <button
            key={t}
            onClick={() => setTone(t)}
            className={`px-4 py-2 rounded-full text-sm border ${
              tone === t ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-white/10 text-neutral-400 hover:text-white"
            }`}
            data-testid={`reconnect-tone-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <button onClick={run} disabled={loading} className="btn-amber mt-8 inline-flex items-center gap-2 disabled:opacity-60" data-testid="reconnect-generate-btn">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Generate reconnect messages
      </button>
      {result && <ResultCard text={result} />}
    </div>
  );
};

export default Tools;
